import { useActivityFeedStore } from '@/core/activity-feed/activity-feed.store';
import { ActivityFeedPersistence } from '@/core/activity-feed/activity-feed.persistence';
import MeService from '@/core/shared/me/me.service';
import { ErrorService } from '@/core/shared/errors/error.service';
import type { ConversationIndex } from '../conversations/conversation-index/conversation-index.type';

export class ActivityFeedService {
  private readonly activityFeedStore = useActivityFeedStore();
  private readonly activityFeedPersistence = new ActivityFeedPersistence();
  private readonly meService = new MeService();
  private visibilityChangeListener: (() => void) | null = null;

  // Message Count (Getters/Setters)
  // ------------------------------------------------------------
  public get unreadMessageCount(): number {
    return this.activityFeedStore.unreadMessageCount;
  }

  public set unreadMessageCount(value: number) {
    this.activityFeedStore.unreadMessageCount = value;
  }

  public get unreadMessageCountPollingIntervalId(): number | null {
    return this.activityFeedStore.unreadMessageCountPollingIntervalId;
  }

  public set unreadMessageCountPollingIntervalId(value: number | null) {
    this.activityFeedStore.unreadMessageCountPollingIntervalId = value;
  }

  public get unreadMessageCountPollingIntervalMs(): number {
    return this.activityFeedStore.unreadMessageCountPollingIntervalMs;
  }

  public set unreadMessageCountPollingIntervalMs(value: number) {
    this.activityFeedStore.unreadMessageCountPollingIntervalMs = value;
  }

  // Message List (Getters/Setters)
  // ------------------------------------------------------------
  public get unreadMessages(): ConversationIndex[] {
    return this.activityFeedStore.unreadMessages;
  }

  public get page(): number {
    return this.activityFeedStore.page;
  }

  public set page(value: number) {
    this.activityFeedStore.page = value;
  }

  public get itemsPerPage(): number {
    return this.activityFeedStore.itemsPerPage;
  }

  public get loadingMessages(): boolean {
    return this.activityFeedStore.loadingMessages;
  }

  // Message Count (Methods)
  // ------------------------------------------------------------
  public async fetchUnreadMessageCount(): Promise<void> {
    const employerId = this.meService.getSelectedEmployerId();
    if (!employerId) {
      return;
    }

    try {
      this.unreadMessageCount =
        await this.activityFeedPersistence.getUnreadMessageCount(employerId);
    } catch (error) {
      ErrorService.captureException(error);
    }
  }

  // Return true if the polling was started, false if there was an error
  // So that the caller can handle the error and retry if needed
  public startPollingUnreadMessageCount(): boolean {
    let result = false;
    try {
      this.stopPollingUnreadMessageCount();

      // Hidden tabs are throttled but not frozen, so an interval left running
      // would keep hitting the API for a badge nobody can see.
      this.visibilityChangeListener = () => {
        if (document.visibilityState === 'visible') {
          this.resumePollingUnreadMessageCount();
        } else {
          this.pauseIntervalUnreadMessageCount();
        }
      };
      document.addEventListener('visibilitychange', this.visibilityChangeListener);

      if (document.visibilityState !== 'hidden') {
        this.resumePollingUnreadMessageCount();
      }
      result = true;
    } catch (error) {
      ErrorService.captureException(error);
    }

    return result;
  }

  public stopPollingUnreadMessageCount(): void {
    try {
      if (this.visibilityChangeListener) {
        document.removeEventListener('visibilitychange', this.visibilityChangeListener);
        this.visibilityChangeListener = null;
      }
      this.pauseIntervalUnreadMessageCount();
    } catch (error) {
      ErrorService.captureException(error);
    }
  }

  private resumePollingUnreadMessageCount(): void {
    this.pauseIntervalUnreadMessageCount();
    this.fetchUnreadMessageCount();
    this.unreadMessageCountPollingIntervalId = window.setInterval(() => {
      this.fetchUnreadMessageCount();
    }, this.unreadMessageCountPollingIntervalMs);
  }

  private pauseIntervalUnreadMessageCount(): void {
    if (this.unreadMessageCountPollingIntervalId) {
      window.clearInterval(this.unreadMessageCountPollingIntervalId);
      this.unreadMessageCountPollingIntervalId = null;
    }
  }

  public async markAllAsRead(): Promise<void> {
    const employerId = this.meService.getSelectedEmployerId();
    if (!employerId) {
      throw new Error('No employer selected');
    }

    await this.activityFeedPersistence.markAllAsRead(employerId);

    this.activityFeedStore.unreadMessageCount = 0;
    this.activityFeedStore.unreadMessages = [];
  }

  // Message List (Methods)
  // ------------------------------------------------------------
  public async fetchUnreadMessages(): Promise<ConversationIndex[]> {
    const employerId = this.meService.getSelectedEmployerId();
    if (!employerId) {
      return []; // No employer ID, so no messages to fetch
    }

    let newMessages: ConversationIndex[] = [];
    try {
      this.activityFeedStore.loadingMessages = true;
      const unreadMessages = await this.activityFeedPersistence.getUnreadMessages(
        employerId,
        this.page,
        this.itemsPerPage,
      );

      if (unreadMessages.conversationIndexes.length > 0) {
        this.activityFeedStore.unreadMessages = [
          ...this.activityFeedStore.unreadMessages,
          ...unreadMessages.conversationIndexes,
        ];
      }
      newMessages = unreadMessages.conversationIndexes;
    } catch (error) {
      ErrorService.captureException(error);
    } finally {
      this.activityFeedStore.loadingMessages = false;
    }

    return newMessages;
  }

  public resetMessageList(): void {
    this.activityFeedStore.unreadMessages = [];
    this.activityFeedStore.page = 1;
  }

  public async loadMoreMessages(): Promise<void> {
    if (this.loadingMessages) {
      return;
    }
    this.page++;
    const newMessages = await this.fetchUnreadMessages();

    // If no new messages were fetched, it means we've paginated beyond the last page.
    // We revert the page increment to prevent advancing further on subsequent calls.
    const newMessagesCount = newMessages.length;
    if (newMessagesCount === 0) {
      this.page--;
    }
  }
}
